import { useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	ExperienceName,
	useAIMateExperienceTracker,
} from '@atlassian/conversation-assistant-instrumentation';
import { performGqlFetch, useQuery } from '@atlassian/conversation-assistant-service';

import { ROVO_AGENTS_FE_TEAM_NAME } from '../../common/constants/private';
import { useSiteContext } from '../site-id';

import type { SitePermissionsResponse, SitePermissionsVariables } from './types';

const AgentPermissionsQuery = `
	query AgentPermissions($cloudId: ID!) {
		atlassianStudio_userSiteContext(cloudId: $cloudId) {
			... on AtlassianStudioUserSiteContextOutput {
				userPermissions {
					isAbleToCreateAgents
				}
			}
		}
	}
`;

export const useAgentSitePermissions = ({
	isEnabled = true,
	overrideSiteId,
	touchpointSource,
}: {
	isEnabled?: boolean;
	overrideSiteId?: string;
	touchpointSource: string;
}) => {
	const { siteId } = useSiteContext();
	const experienceTracker = useAIMateExperienceTracker({
		touchpointSource,
	});

	const siteIdToUse = overrideSiteId ?? siteId;

	const query = useQuery({
		queryKey: ['fetchSitePermissions', siteIdToUse],
		queryFn: async () => {
			experienceTracker.start({
				id: ExperienceName.FETCH_AGENT_SITE_PERMISSIONS,
				name: ExperienceName.FETCH_AGENT_SITE_PERMISSIONS,
			});
			return await performGqlFetch<SitePermissionsResponse, SitePermissionsVariables>({
				gqlQuery: AgentPermissionsQuery,
				variables: {
					cloudId: siteIdToUse,
				},
			});
		},
		enabled: isEnabled,
		onSuccess: () => {
			experienceTracker.succeed({
				name: ExperienceName.FETCH_AGENT_SITE_PERMISSIONS,
			});
		},
		onError: (error) => {
			experienceTracker.fail({
				name: ExperienceName.FETCH_AGENT_SITE_PERMISSIONS,
				error,
				teamName: ROVO_AGENTS_FE_TEAM_NAME,
			});
		},
	});

	return query;
};

// memoized wrapper around useAgentSitePermissions->isAbleToCreateAgents and its associated fgs
export const useCanCreateAgents = ({
	siteId,
	touchpointSource,
}: {
	siteId: string;
	touchpointSource: string;
}) => {
	const { data: sitePermissions, isFetching: isFetchingSitePermissions } = useAgentSitePermissions({
		isEnabled: fg('agent_studio_fe_permissions_settings_m1'),
		overrideSiteId: siteId,
		touchpointSource,
	});

	const { isAbleToCreateAgents, isFetchingPermissions } = useMemo(() => {
		if (fg('agent_studio_fe_permissions_settings_m1')) {
			return {
				isAbleToCreateAgents:
					sitePermissions?.data?.atlassianStudio_userSiteContext?.userPermissions
						?.isAbleToCreateAgents ?? false,
				isFetchingPermissions: isFetchingSitePermissions,
			};
		}

		// When create permissions is not enabled, we always allow the user to create agents
		return {
			isAbleToCreateAgents: true,
			isFetchingPermissions: false,
		};
	}, [
		isFetchingSitePermissions,
		sitePermissions?.data?.atlassianStudio_userSiteContext?.userPermissions?.isAbleToCreateAgents,
	]);

	return { isAbleToCreateAgents, isFetchingPermissions };
};
